import React, { useEffect, useState } from "react";

import { Button, Icon, IconButton, Modal } from "../components/ui";
import { formatBytes } from "./chatFormat";

/**
 * Full-size viewer for images sent in a thread.
 *
 * Built on the shared Modal so it inherits the focus trap, Escape handling,
 * scroll lock and the phone sheet treatment rather than re-implementing an
 * overlay. Arrow keys step through the set the user clicked into, which is
 * why the caller passes the whole image array plus a starting index instead
 * of a single attachment.
 */
const ImageLightbox = ({ images = [], startIndex = 0, onClose }) => {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => setIndex(startIndex), [startIndex, images]);

  const count = images.length;
  const current = images[index];

  useEffect(() => {
    if (count < 2) return undefined;

    const onKey = (event) => {
      if (event.key === "ArrowRight") setIndex((i) => (i + 1) % count);
      if (event.key === "ArrowLeft") setIndex((i) => (i - 1 + count) % count);
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [count]);

  if (!current) return null;

  return (
    <Modal
      open
      onClose={onClose}
      variant="dialog"
      size="lg"
      className="rc-chat-lightbox"
      title={current.name || "Image"}
      description={
        [current.size ? formatBytes(current.size) : null, count > 1 ? `${index + 1} of ${count}` : null]
          .filter(Boolean)
          .join(" · ") || undefined
      }
      footer={
        <Button
          href={current.url}
          download={current.name || undefined}
          variant="secondary"
          size="sm"
        >
          <Icon name="Download" size="sm" /> Download
        </Button>
      }
    >
      <div className="rc-chat-lightbox__stage">
        {count > 1 && (
          <IconButton
            className="rc-chat-lightbox__nav rc-chat-lightbox__nav--prev"
            label="Previous image"
            onClick={() => setIndex((i) => (i - 1 + count) % count)}
          >
            <Icon name="ChevronLeft" size="sm" />
          </IconButton>
        )}

        <img src={current.url} alt={current.name || ""} />

        {count > 1 && (
          <IconButton
            className="rc-chat-lightbox__nav rc-chat-lightbox__nav--next"
            label="Next image"
            onClick={() => setIndex((i) => (i + 1) % count)}
          >
            <Icon name="ChevronRight" size="sm" />
          </IconButton>
        )}
      </div>
    </Modal>
  );
};

export default ImageLightbox;
